import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined, SearchOutlined } from "@mui/icons-material";
import { PlatformIntegration } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserIntegrationsQuery,
  useGetAssignableIntegrationsQuery,
} from "features/adminUsers/adminUsers";
import {
  getPlatformIntegrationDisplayName,
  getPlatformIntegrationTooltipTitle,
  parseIntegrationMethodPrefix,
} from "utils/platformIntegrationDisplay";

interface Props {
  userId: number | null;
  isCreateMode?: boolean;
  isIntegrationDashboardUser: boolean;
  canEdit: boolean;
  isCalibrateManager: boolean;
  integrationDraft?: Record<number, boolean>;
  onIntegrationDraftChange?: (
    matchId: number,
    hasAccess: boolean,
    serverHasAccess: boolean,
  ) => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
  onSaveComplete?: () => void;
  onSaveAll?: () => void;
  onCreateUser?: () => void;
  createUserDisabled?: boolean;
  hasAnyChanges?: boolean;
  isSaving?: boolean;
}

const METHOD_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  Webhook: { bg: "rgba(21,101,192,0.10)", color: "#1565c0" },
  Scheduler: { bg: "rgba(46,125,50,0.10)", color: "#2e7d32" },
  "S3-triggered": { bg: "rgba(230,81,0,0.10)", color: "#e65100" },
};

function IntegrationRow({
  integration,
  checked,
  disabled,
  onToggle,
}: {
  integration: PlatformIntegration;
  checked: boolean;
  disabled: boolean;
  onToggle: (integration: PlatformIntegration) => void;
}) {
  const { methodType } = parseIntegrationMethodPrefix(
    integration.integration_name,
  );
  const line = getPlatformIntegrationDisplayName(integration);
  const methodColors = methodType
    ? (METHOD_TYPE_COLORS[methodType] ?? {
        bg: "rgba(0,0,0,0.07)",
        color: "text.secondary",
      })
    : null;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      py={0.6}
      px={0.5}
      sx={{
        borderRadius: 1,
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Checkbox
          checked={checked}
          size="small"
          disableRipple
          disabled={disabled}
          onChange={() => onToggle(integration)}
          sx={{ p: 0 }}
        />
      </Box>
      <Box flex={1} minWidth={0}>
        <Tooltip
          title={getPlatformIntegrationTooltipTitle(integration)}
          enterDelay={300}
          placement="top-start"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ minWidth: 0 }}
          >
            {methodColors && (
              <Chip
                label={methodType}
                size="small"
                sx={{
                  height: 16,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: methodColors.bg,
                  color: methodColors.color,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              fontWeight={700}
              noWrap
              component="span"
            >
              {line}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      {!integration.enabled && (
        <Chip
          label="Disabled"
          size="small"
          sx={{
            height: 16,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: "rgba(211,47,47,0.10)",
            color: "error.dark",
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
}

export default function UserManagementIntegrationsTab({
  userId,
  isCreateMode = false,
  isIntegrationDashboardUser,
  canEdit,
  isCalibrateManager,
  integrationDraft,
  onIntegrationDraftChange,
  onSuccess,
  onSaveComplete,
  onSaveAll,
  onCreateUser,
  createUserDisabled = false,
  hasAnyChanges = false,
  isSaving = false,
}: Props) {
  const { data: userData, isLoading: isLoadingUser } =
    useGetAdminUserIntegrationsQuery(userId!, { skip: !userId });
  const { data: assignableData, isLoading: isLoadingAssignable } =
    useGetAssignableIntegrationsQuery(undefined, { skip: !isCreateMode });

  const data = isCreateMode ? assignableData : userData;
  const integrations = data?.integrations ?? [];
  const isLoading = isCreateMode ? isLoadingAssignable : isLoadingUser;
  const [search, setSearch] = useState("");

  const readOnly = isCalibrateManager && !canEdit;

  const getChecked = (integration: PlatformIntegration) =>
    integrationDraft?.[integration.id] ?? integration.has_access;

  const handleToggle = (integration: PlatformIntegration) => {
    if (readOnly) return;
    const next = !getChecked(integration);
    onIntegrationDraftChange?.(integration.id, next, integration.has_access);
  };

  const hasUnsaved = integrations.some(
    (i) => (integrationDraft?.[i.id] ?? i.has_access) !== i.has_access,
  );

  if (!isIntegrationDashboardUser) {
    return (
      <Box
        px={2.5}
        pt={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Enable Integration Dashboard User role to manage integration access.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box px={2.5} pt={2}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={64}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  const q = search.toLowerCase();
  const filtered = search.trim()
    ? integrations.filter(
        (i) =>
          getPlatformIntegrationDisplayName(i).toLowerCase().includes(q) ||
          i.integration_name.toLowerCase().includes(q) ||
          i.source_vendor.toLowerCase().includes(q) ||
          i.destination_vendor.toLowerCase().includes(q) ||
          i.owner_username.toLowerCase().includes(q),
      )
    : integrations;

  const ordered = [...filtered].sort((a, b) => {
    const aServer = a.has_access === true;
    const bServer = b.has_access === true;
    if (aServer === bServer) return 0;
    return aServer ? -1 : 1;
  });

  return (
    <>
      <Box px={2} pt={1} pb={0.5} display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="text.secondary">
          Check the box to grant this user access to an integration.
        </Typography>
        <Tooltip title="Checked integrations will be visible to the user on the web platform.">
          <InfoOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
        </Tooltip>
      </Box>
      <Box px={2} pt={0.5} pb={1}>
        <TextField
          size="small"
          placeholder="Search integrations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          inputProps={{ autoComplete: "off" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1, fontSize: 13 } }}
        />
      </Box>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0, px: 2, pb: 1 }}>
        {ordered.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mt={2}
          >
            {integrations.length === 0
              ? "No integrations available."
              : "No integrations match your search."}
          </Typography>
        ) : (
          ordered.map((integration) => (
            <IntegrationRow
              key={integration.id}
              integration={integration}
              checked={getChecked(integration)}
              disabled={readOnly || !canEdit}
              onToggle={handleToggle}
            />
          ))
        )}
      </CustomScrollbars>
      <Box px={2} pb={2} pt={1} sx={{ flexShrink: 0 }}>
        {hasAnyChanges && (
          <Typography
            variant="caption"
            fontWeight={600}
            display="block"
            sx={{ mb: 1, color: "#ad1457" }}
          >
            Unsaved changes
          </Typography>
        )}
        {isCreateMode && onCreateUser ? (
          <Button
            variant="contained"
            fullWidth
            onClick={onCreateUser}
            disabled={createUserDisabled || isSaving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1,
              py: 0.9,
            }}
          >
            {isSaving ? "Creating…" : "Create User"}
          </Button>
        ) : (
          !isCreateMode &&
          onSaveAll && (
            <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onSaveAll}
                  disabled={!canEdit || !hasAnyChanges || isSaving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1,
                    py: 0.9,
                  }}
                >
                  {isSaving ? "Saving…" : "Save Changes"}
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Box>
    </>
  );
}
